import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Chapter, Reveal, MaskLines } from "./Reveal";
import { toast } from "@/components/ui/sonner";
import { ArrowUpRight, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = ["Quidory", "Al Yakhour Media", "General Inquiry", "Partnership"];

const EMAILS = [
  { label: "General", value: "info@alyakhour.com" },
  { label: "Media & Agency", value: "media@alyakhour.com" },
  { label: "Founder", value: "saher@alyakhour.com" },
];

const initial = { name: "", email: "", reason: "General Inquiry", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete all fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks — your message has reached us. We'll be in touch.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please email info@alyakhour.com directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const fieldCls =
    "w-full rounded-sm border border-hairline bg-ink px-4 py-3 font-body text-sm text-cloud placeholder:text-fog/60 outline-none transition-colors duration-300 focus:border-steel-light";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="border-t border-hairline bg-surface/30"
    >
      <div className="mx-auto max-w-[1400px] px-6 py-28 lg:px-12 lg:py-40">
        <Chapter index="05" label="Contact" className="mb-10" />

        <div className="grid grid-cols-1 gap-14 lg:grid-cols-2 lg:gap-24">
          {/* Left: intro + direct emails */}
          <div>
            <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tighter text-cloud sm:text-5xl">
              <MaskLines lines={["Let's start", "a conversation."]} />
            </h2>
            <Reveal delay={0.15}>
              <p className="mt-8 max-w-md font-body text-base font-light leading-relaxed text-fog">
                Whether it&apos;s Quidory, a media engagement, or a partnership across
                the group — tell us what you&apos;re building. Prefer email? Reach us
                directly below.
              </p>
            </Reveal>

            <Reveal delay={0.25} className="mt-10 space-y-4">
              {EMAILS.map((e) => (
                <a
                  key={e.value}
                  href={`mailto:${e.value}`}
                  data-testid={`contact-email-${e.label.split(" ")[0].toLowerCase()}`}
                  className="group flex items-center justify-between border-b border-hairline pb-4 transition-colors duration-300 hover:border-steel-light"
                >
                  <span className="flex flex-col">
                    <span className="font-body text-[11px] uppercase tracking-[0.25em] text-fog/70">
                      {e.label}
                    </span>
                    <span className="font-display text-lg font-medium text-cloud transition-colors group-hover:text-steel-light">
                      {e.value}
                    </span>
                  </span>
                  <ArrowUpRight
                    size={18}
                    className="text-fog transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-steel-light"
                  />
                </a>
              ))}
            </Reveal>
          </div>

          {/* Right: form */}
          <Reveal delay={0.15}>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-sm border border-hairline bg-surface p-6 sm:p-8"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block font-body text-[11px] uppercase tracking-[0.2em] text-fog">
                    Name
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={set("name")}
                    data-testid="contact-name-input"
                    placeholder="Your name"
                    className={fieldCls}
                  />
                </div>
                <div>
                  <label className="mb-2 block font-body text-[11px] uppercase tracking-[0.2em] text-fog">
                    Email
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    data-testid="contact-email-input"
                    placeholder="you@company.com"
                    className={fieldCls}
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="mb-2 block font-body text-[11px] uppercase tracking-[0.2em] text-fog">
                  Reason for inquiry
                </label>
                <select
                  value={form.reason}
                  onChange={set("reason")}
                  data-testid="contact-reason-select"
                  className={`${fieldCls} appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2216%22 height=%2216%22 fill=%22none%22 stroke=%22%2394A3B8%22 stroke-width=%222%22><path d=%22M4 6l4 4 4-4%22/></svg>')] bg-[length:16px] bg-[right_1rem_center] bg-no-repeat pr-10`}
                >
                  {REASONS.map((r) => (
                    <option key={r} value={r} className="bg-ink text-cloud">
                      {r}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-5">
                <label className="mb-2 block font-body text-[11px] uppercase tracking-[0.2em] text-fog">
                  Message
                </label>
                <textarea
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  data-testid="contact-message-input"
                  placeholder="Tell us a little about what you need…"
                  className={`${fieldCls} resize-none`}
                />
              </div>

              <motion.button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                whileTap={{ scale: 0.98 }}
                className="group mt-7 inline-flex w-full items-center justify-center gap-2.5 rounded-sm bg-steel px-7 py-3.5 font-body text-sm font-semibold text-white transition-colors duration-300 hover:bg-steel-light disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 size={17} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send message
                    <Send
                      size={16}
                      className="transition-transform duration-300 group-hover:translate-x-0.5"
                    />
                  </>
                )}
              </motion.button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
